/**
 * The last few appetite answers, kept so an agent can look one up again.
 *
 * WHY THE WHOLE ANSWER AND NOT JUST THE QUESTION. Re-running a search costs an
 * AI call and a second of waiting, and — because the appetite bundle is
 * refreshed periodically — can quietly return something different from what the
 * agent is trying to check. "What did it say about that roofer on Tuesday" has
 * one honest answer, and it is the one they were actually shown.
 *
 * ⚠️ STORED IN THE BROWSER, DELIBERATELY FOR NOW. It works the moment it ships,
 * needs no migration, and history is inherently personal. The cost is real and
 * worth stating: it does not follow an agent to another machine, and clearing
 * site data loses it. When that starts to matter it wants a table — the shape
 * here is already the shape a row would take.
 *
 * A question can name an insured, so this never leaves the device.
 */
import type { AppetiteAnswer } from '@commpass/shared';

const KEY = 'aas.recentAppetite.v1';
const MAX = 12;

export interface RecentSearch {
  at: number;
  question: string;
  /** The answer exactly as it was shown, so revisiting it cannot rewrite history. */
  answer: AppetiteAnswer;
}

export function loadRecent(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as RecentSearch[];
    return Array.isArray(list) ? list.filter((r) => r?.question && r?.answer) : [];
  } catch {
    // A corrupt entry must not take the chat down with it.
    return [];
  }
}

export function rememberSearch(question: string, answer: AppetiteAnswer): void {
  try {
    const q = question.trim();
    if (!q) return;
    // Asking the same thing twice moves it up rather than filling the list.
    const rest = loadRecent().filter((r) => r.question.trim().toLowerCase() !== q.toLowerCase());
    const next = [{ at: Date.now(), question: q, answer }, ...rest].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private browsing, a full quota, a locked-down device — none of which is a
    // reason to fail the search that just succeeded.
  }
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/** "2 hours ago" reads better than a timestamp in a list this short. */
export function ago(at: number): string {
  const mins = Math.round((Date.now() - at) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}
